import React, { useEffect, useRef, useState } from "react";
import { Mic, RefreshCw, Volume2 } from "lucide-react";
import { Button } from "./ui/button";

const RANGE = { amber: "accent-amber-500", violet: "accent-violet-500" };
const ICON = { amber: "text-amber-300", violet: "text-violet-300" };

/** Voz en off de un canal: interruptor, voz, velocidad y muestra. Los ajustes viven en el form de cada radar. */
export const VoicePicker = ({ form, setForm, voices, loadSample, onError, accent, testid, hint }) => {
  const [sample, setSample] = useState({ loading: false, url: null });
  const audioRef = useRef(null);

  useEffect(() => () => { if (sample.url) URL.revokeObjectURL(sample.url); }, [sample.url]);

  const play = async () => {
    setSample((s) => ({ ...s, loading: true }));
    try {
      const blob = await loadSample(form.voice, Number(form.voice_rate));
      setSample({ loading: false, url: URL.createObjectURL(blob) });
      setTimeout(() => audioRef.current?.play(), 50);
    } catch (e) {
      setSample({ loading: false, url: null });
      onError("El servicio de voz no respondió. Si sigue así, los Shorts saldrán solo con música.");
    }
  };

  return (
    <div className="p-4 rounded-xl bg-black/30 border border-white/5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase font-bold text-slate-400 flex items-center gap-1.5">
          <Mic className={`w-3.5 h-3.5 ${ICON[accent]}`} /> Voz en off
        </span>
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          role="switch"
          aria-checked={!!form.voice_enabled}
          onClick={() => setForm((f) => ({ ...f, voice_enabled: !f.voice_enabled }))}
          className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${form.voice_enabled ? "bg-emerald-500" : "bg-slate-700"}`}
        >
          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${form.voice_enabled ? "left-5" : "left-1"}`} />
        </button>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <label className="block flex-1 min-w-0">
          <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Voz</span>
          <select
            data-testid={`${testid}-select`}
            value={form.voice}
            disabled={!form.voice_enabled}
            onChange={(e) => setForm((f) => ({ ...f, voice: e.target.value }))}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-sm text-white disabled:opacity-50"
          >
            {Object.entries(voices).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="block sm:w-36">
          <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">
            Velocidad ({form.voice_rate > 0 ? "+" : ""}{form.voice_rate}%)
          </span>
          <input
            type="range"
            min={-20}
            max={30}
            step={2}
            value={form.voice_rate}
            disabled={!form.voice_enabled}
            onChange={(e) => setForm((f) => ({ ...f, voice_rate: Number(e.target.value) }))}
            className={`w-full ${RANGE[accent]}`}
          />
        </label>
        <Button
          type="button"
          size="sm"
          data-testid={`${testid}-sample`}
          onClick={play}
          disabled={!form.voice_enabled || sample.loading}
          className="h-9 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center gap-1.5"
        >
          {sample.loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />} Escuchar
        </Button>
      </div>
      {sample.url && <audio ref={audioRef} src={sample.url} controls className="w-full h-8" />}
      <p className="text-[10px] text-slate-500">{hint}</p>
    </div>
  );
};
